import { useCallback, useRef } from 'react';
import { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { CalDateInfo, SlotInfo, SlotStatus, SlotType } from '../../../types/schedule.types';
import { SceneInfo } from '../../../types/scene.types';
import { parseDate } from '../../../utils/dateUtils';
import {
  normalizeToSortedIds,
  buildDisplaySegments,
  computeSlotLocationVisibilityPatchesForOrderedIds,
  mergeCalDateShift,
  getVirtualSoltBundle,
} from '../../../utils/calDateSlotsSortUtils';
import { resolveInsertIndexForExternalDrop } from '../utils/slotDragUtils';
import { getClipboardDroppableId } from '../Components/TabPages/ClipboardPanel/ClipboardPanel';
import { slotsToClipboardItems } from './useScheduleClipboard';
import type { ClipboardScheduleItem } from './useScheduleClipboard';
import { restoreClipboardItemsToDate } from './restoreClipboardItemsToDate';
import { isValidCalendarDropTarget } from '../utils/dragEndClipboardDrop';
import {
  buildMovedCalDateList,
  buildMovedSlotList,
  commitCrossDateMoveCache,
  mergeServerCalDates,
} from '../utils/syncCrossDateMoveCache';
import { getCalDatesByScheduleId, moveSlots } from '../../../api/schedule';

export interface UseCrossDateDnDParams {
  scheduleID: string | undefined;
  currentDate: Date | null;
  formatDateToString: (d: Date) => string;
  measureCalendarDayContentWidthByDateStr: (dateStr: string) => number | null;
  optimisticSlots: SlotInfo[];
  setOptimisticSlots: React.Dispatch<React.SetStateAction<SlotInfo[]>>;
  optimisticCalDates: CalDateInfo[];
  setOptimisticCalDates: React.Dispatch<React.SetStateAction<CalDateInfo[]>>;
  getDateCalDate: (dateStr: string) => CalDateInfo | undefined;
  getCalendarRowIdsForDateStr: (dateStr: string) => string[];
  getSceneInfo: (sceneID: string | undefined) => SceneInfo | undefined;
  extractDateFromOverIdFn: (
    overId: string,
    overData?: { dateStr?: string } | null,
  ) => string | null | undefined;
  checkSlotDuplicateFn: (
    sceneID: string,
    dateStr: string,
    excludeSlotID?: string,
    scheduleIDParam?: string,
  ) => boolean;
  handleInDateSorting: (
    activeId: string,
    overId: string,
    dateStr: string,
    bundleIdsParam?: string[] | null,
  ) => Promise<void>;
  updateCalDateSlotsSort: (
    dateStr: string,
    additionalSoltIds?: string[],
    excludeSoltIds?: string[],
    replaceIds?: string[],
    calDateShift?: string,
  ) => Promise<void>;
  refreshScheduleCalendarData: () => Promise<void>;
  createSlotData: (scheduleID: string, data: Partial<SlotInfo>) => Promise<string | undefined>;
  updateSlotData: (
    scheduleId: string,
    slotId: string,
    updates: Partial<SlotInfo>,
  ) => Promise<void>;
  deleteSlotData: (scheduleId: string, slotId: string) => Promise<void>;
  deleteCalDateData: (scheduleId: string, calDateId: string) => Promise<void>;
  upsertCalDateData: (scheduleId: string, data: Partial<CalDateInfo>) => Promise<void>;
  getSlotCache: (scheduleID: string) => { slots?: SlotInfo[] } | null;
  setSlotCache: (scheduleID: string, data: Partial<{ slots: SlotInfo[] }>) => void;
  getCalDateCache: (scheduleID: string) => { calDates?: CalDateInfo[] } | null;
  setCalDateCache: (scheduleID: string, data: Partial<{ calDates: CalDateInfo[] }>) => void;
  clipboardAddGroup: (input: {
    sourceDateStr: string;
    items: ClipboardScheduleItem[];
    sourceCalDateShift?: string;
  }) => void;
  clipboardRemoveGroupAt: (groupIndex: number) => void;
  clipboardRemoveGroupItemAt: (groupIndex: number, itemIndex: number) => void;
  clipboardRemoveGroupItemSlotAt: (groupIndex: number, itemIndex: number, slotIndex: number) => void;
  setCurrentDate: (d: Date) => void;
  setServerSyncedField: (key: string, value: string) => void;
  setShouldHideDragOverlay: React.Dispatch<React.SetStateAction<boolean>>;
  setWholeDayOverlayWidth: React.Dispatch<React.SetStateAction<number | null>>;
  setSceneDragOverlayWidth: React.Dispatch<React.SetStateAction<number | null>>;
  setCalendarInsertHighlight: React.Dispatch<
    React.SetStateAction<{ dateStr: string; insertIndex: number } | null>
  >;
  setActiveDragId: React.Dispatch<React.SetStateAction<string | null>>;
  setActiveDragType: React.Dispatch<
    React.SetStateAction<'slot' | 'scene' | 'wholeDay' | 'clipboard' | 'clipboardGroup' | null>
  >;
  setActiveDragData: React.Dispatch<React.SetStateAction<any>>;
  setSourceDateCell: React.Dispatch<React.SetStateAction<string | null>>;
  setDisabledDateCells: React.Dispatch<React.SetStateAction<Set<string>>>;
  setInvalidDropTargets: React.Dispatch<React.SetStateAction<Set<string>>>;
  setDragOverDateCell: React.Dispatch<React.SetStateAction<string | null>>;
  setActiveSortingId: React.Dispatch<React.SetStateAction<string | null>>;
  setActiveSortingType: React.Dispatch<React.SetStateAction<'slot' | null>>;
  setActiveSortingDate: React.Dispatch<React.SetStateAction<string | null>>;
  setActiveSortingDragLevel: React.Dispatch<React.SetStateAction<'slot' | 'note' | null>>;
  setSlotOverlayWidth: React.Dispatch<React.SetStateAction<number | null>>;
  setActiveSlotDragBundleIds: React.Dispatch<React.SetStateAction<string[] | null>>;
  /** “显示所有地点”模式：UI 忽略 slotLocationVisibility（影响分组/拖拽 bundle） */
  forceShowAllLocations?: boolean;
}

export function useCrossDateDnD(p: UseCrossDateDnDParams) {
  const slotDragBundleIdsRef = useRef<string[] | null>(null);
  const {
    scheduleID,
    currentDate,
    formatDateToString,
    measureCalendarDayContentWidthByDateStr,
    optimisticSlots,
    setOptimisticSlots,
    optimisticCalDates,
    setOptimisticCalDates,
    getDateCalDate,
    getCalendarRowIdsForDateStr,
    getSceneInfo,
    extractDateFromOverIdFn,
    checkSlotDuplicateFn,
    handleInDateSorting,
    updateCalDateSlotsSort,
    refreshScheduleCalendarData,
    createSlotData,
    deleteSlotData,
    deleteCalDateData,
    upsertCalDateData,
    getSlotCache,
    setSlotCache,
    getCalDateCache,
    setCalDateCache,
    clipboardAddGroup,
    clipboardRemoveGroupAt,
    clipboardRemoveGroupItemAt,
    clipboardRemoveGroupItemSlotAt,
    setCurrentDate,
    setServerSyncedField,
    setShouldHideDragOverlay,
    setWholeDayOverlayWidth,
    setSceneDragOverlayWidth,
    setCalendarInsertHighlight,
    setActiveDragId,
    setActiveDragType,
    setActiveDragData,
    setSourceDateCell,
    setDisabledDateCells,
    setInvalidDropTargets,
    setDragOverDateCell,
    setActiveSortingId,
    setActiveSortingType,
    setActiveSortingDate,
    setActiveSortingDragLevel,
    setSlotOverlayWidth,
    setActiveSlotDragBundleIds,
    forceShowAllLocations = false,
  } = p;

  const handleCrossDateDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      const id = active.id as string;
      const data = active.data.current;

      // 重置隐藏标志和整日预览宽度
      setShouldHideDragOverlay(false);
      setWholeDayOverlayWidth(null);
      setSceneDragOverlayWidth(null);
      setCalendarInsertHighlight(null);

      // 检查是否是 slot
      if (data?.type === 'slot') {
        setActiveDragId(id);
        setActiveDragType(data.type);
        setActiveDragData(data);

        const sourceDateStr = data.sourceDateStr as string | undefined;
        let bundleIds: string[] = [id];

        if (sourceDateStr && data.dragLevel === 'slot') {
          const ae = event.activatorEvent as MouseEvent | PointerEvent | undefined;
          const el = ae?.target as HTMLElement | undefined;
          const handleEl = el?.closest?.('[data-slot-drag-handle]') as HTMLElement | null;
          const mode = handleEl?.getAttribute('data-slot-drag-handle');
          if (mode === 'bundle') {
            const calDate = getDateCalDate(sourceDateStr);
            if (calDate) {
              const daySlots = optimisticSlots.filter((pt) => {
                if (!pt.slotDate) return false;
                return pt.slotDate.split('T')[0] === sourceDateStr;
              });
              const getSceneInfoMinimal = (sceneID: string | undefined) => {
                const s = getSceneInfo(sceneID);
                return s ? { sceneLocation: s.sceneLocation } : undefined;
              };
              const segments = buildDisplaySegments(
                daySlots,
                calDate.calDateSlotsSort,
                sourceDateStr,
                getSceneInfoMinimal,
                { forceShowAllLocations: forceShowAllLocations },
              );
              const orderedIds = segments.flatMap((s) => s.items);
              const slotMap = new Map(daySlots.map((t) => [t.slotID, t]));
              bundleIds = getVirtualSoltBundle(orderedIds, id, slotMap, { forceShowAllLocations: forceShowAllLocations });
            }
          }
        }

        slotDragBundleIdsRef.current = bundleIds;
        setActiveSlotDragBundleIds(bundleIds);

        if (sourceDateStr) {
          const [y, m, d] = sourceDateStr.split('-').map(Number);
          const cell = document.querySelector(
            `.calendar-day-cell[data-year="${y}"][data-month="${m}"][data-day="${d}"]`,
          ) as HTMLElement | null;
          const contentEl = cell?.querySelector('.calendar-day-content') as HTMLElement | null;
          const ow = (contentEl ?? cell)?.getBoundingClientRect().width ?? null;
          setSlotOverlayWidth(ow != null && ow > 0 ? ow : null);
          setWholeDayOverlayWidth(null);

          setSourceDateCell(sourceDateStr);
          setActiveSortingId(id);
          setActiveSortingType(data.type);
          setActiveSortingDate(sourceDateStr);
          setActiveSortingDragLevel(data.dragLevel ?? null);
        } else {
          setSlotOverlayWidth(null);
        }

        setDisabledDateCells(new Set());
      } else {
        slotDragBundleIdsRef.current = null;
        setActiveSlotDragBundleIds(null);
        setSlotOverlayWidth(null);

        if (data?.type === 'scene') {
          setActiveDragId(id);
          setActiveDragType(data.type);
          setActiveDragData(data);
          setSourceDateCell(null);
          setDisabledDateCells(new Set());
          const refStr = currentDate ? formatDateToString(currentDate) : null;
          let ow = refStr ? measureCalendarDayContentWidthByDateStr(refStr) : null;
          if (ow == null) {
            const first = document.querySelector(
              '.calendar-day-cell .calendar-day-content',
            ) as HTMLElement | null;
            const fw = first?.getBoundingClientRect().width;
            ow = fw != null && fw > 0 ? fw : null;
          }
          setSceneDragOverlayWidth(ow);
        } else if (data?.type === 'clipboardGroup') {
          setActiveDragId(id);
          setActiveDragType('clipboardGroup');
          setActiveDragData(data);
          setSourceDateCell(null);
          setDisabledDateCells(new Set());
        } else if (data?.type === 'clipboard') {
          setActiveDragId(id);
          setActiveDragType('clipboard');
          setActiveDragData(data);
          setSourceDateCell(null);
          setDisabledDateCells(new Set());
        } else if (data?.type === 'wholeDay') {
          setActiveDragId(id);
          setActiveDragType('wholeDay');
          setActiveDragData(data);
          setSlotOverlayWidth(null);
          const sourceDateStr = data.sourceDateStr;
          if (sourceDateStr) {
            setSourceDateCell(sourceDateStr);
            setDisabledDateCells(new Set([sourceDateStr]));
          } else {
            setDisabledDateCells(new Set());
          }
          if (sourceDateStr) {
            const [y, m, d] = sourceDateStr.split('-').map(Number);
            const cell = document.querySelector(
              `.calendar-day-cell[data-year="${y}"][data-month="${m}"][data-day="${d}"]`
            ) as HTMLElement | null;
            const cellWidth = cell ? cell.getBoundingClientRect().width : null;
            setWholeDayOverlayWidth(cellWidth);
          }
        }
      }
    },
    [
      getDateCalDate,
      optimisticSlots,
      getSceneInfo,
      forceShowAllLocations,
      currentDate,
      formatDateToString,
      measureCalendarDayContentWidthByDateStr,
      setShouldHideDragOverlay,
      setWholeDayOverlayWidth,
      setSceneDragOverlayWidth,
      setCalendarInsertHighlight,
      setActiveDragId,
      setActiveDragType,
      setActiveDragData,
      setActiveSlotDragBundleIds,
      setSlotOverlayWidth,
      setSourceDateCell,
      setDisabledDateCells,
      setActiveSortingId,
      setActiveSortingType,
      setActiveSortingDate,
      setActiveSortingDragLevel,
    ],
  );

  // 处理跨日期拖曳悬停
  const handleCrossDateDragOver = useCallback((event: DragOverEvent) => {
    const { over, active } = event;
    
    // 检查是否是 slot 的拖曳
    const dragData = active.data.current;
    if (dragData && dragData.type === 'slot') {
      const sourceDateStr = dragData.sourceDateStr;
      const newDisabled = new Set<string>();
      const newInvalidTargets = new Set<string>();
      
      if (!over) {
        // 拖曳到空白区域，禁用源日期排序
        if (sourceDateStr) {
          newDisabled.add(sourceDateStr);
        }
        setDragOverDateCell(null);
        setCalendarInsertHighlight(null);
      } else {
        // 从 over.id 中提取日期字符串
        const overDateStr = extractDateFromOverIdFn(over.id.toString(), over.data?.current as any);
        
        if (!overDateStr) {
          // 无法提取日期，禁用源日期排序
          if (sourceDateStr) {
            newDisabled.add(sourceDateStr);
          }
          setDragOverDateCell(null);
          setCalendarInsertHighlight(null);
        } else {
          if (overDateStr !== sourceDateStr) {
            // 跨日期拖曳，禁用源日期排序
            if (sourceDateStr) {
              newDisabled.add(sourceDateStr);
            }
            
            // 检查是否重复：单卡跨日仍禁止落点；地点头多卡整包拖拽与整日一致，允许落点（dragEnd 会删重复项、只移入未重复）
            if (dragData.type === 'slot') {
              const bundleFromRef = slotDragBundleIdsRef.current;
              const moveIds =
                bundleFromRef && bundleFromRef.length > 0
                  ? [...bundleFromRef]
                  : dragData.slot
                    ? [dragData.slot.slotID]
                    : [String(active.id)];
              const isMultiSoltLocationBundle =
                bundleFromRef != null && bundleFromRef.length > 1;
              if (!isMultiSoltLocationBundle) {
                const slotsToCheck = moveIds
                  .map((tid) => optimisticSlots.find((t) => t.slotID === tid))
                  .filter((t): t is SlotInfo => t != null)
                  .filter((t) => t.slotType === SlotType.SHOOT || !t.slotType);
                for (const slot of slotsToCheck) {
                  if (!slot.slotBelongSceneID || !slot.slotBelongScheduleID) continue;
                  const isDuplicate = checkSlotDuplicateFn(
                    slot.slotBelongSceneID,
                    overDateStr,
                    slot.slotID,
                    slot.slotBelongScheduleID
                  );
                  if (isDuplicate) {
                    newInvalidTargets.add(overDateStr);
                    break;
                  }
                }
              }
            }
          }
          // 如果 overDateStr === sourceDateStr，不禁用（同一天内排序）
          
          setDragOverDateCell(overDateStr);
          if (overDateStr === sourceDateStr || newInvalidTargets.has(overDateStr)) {
            setCalendarInsertHighlight(null);
          } else {
            const rowN = getCalendarRowIdsForDateStr(overDateStr).length;
            const resolved = resolveInsertIndexForExternalDrop(
              over.id.toString(),
              over.data?.current,
              optimisticSlots,
              rowN,
            );
            if (resolved && resolved.dateStr === overDateStr) {
              setCalendarInsertHighlight({ dateStr: resolved.dateStr, insertIndex: resolved.insertIndex });
            } else {
              setCalendarInsertHighlight(null);
            }
          }
        }
      }
      
      setDisabledDateCells(newDisabled);
      setInvalidDropTargets(newInvalidTargets);
    } else if (dragData && dragData.type === 'scene') {
      // 处理场次拖拽悬停
      const newInvalidTargets = new Set<string>();
      
      if (!over) {
        setDragOverDateCell(null);
        setCalendarInsertHighlight(null);
      } else {
        const overDateStr = extractDateFromOverIdFn(over.id.toString(), over.data?.current as any);
        if (!overDateStr) {
          setDragOverDateCell(null);
          setCalendarInsertHighlight(null);
        } else {
          // 检查是否重复
          if (dragData.scene && dragData.scheduleID) {
            const isDuplicate = checkSlotDuplicateFn(
              dragData.scene.sceneID,
              overDateStr,
              undefined,
              dragData.scheduleID
            );
            if (isDuplicate) {
              newInvalidTargets.add(overDateStr);
              setCalendarInsertHighlight(null);
            } else {
              const rowN = getCalendarRowIdsForDateStr(overDateStr).length;
              const resolved = resolveInsertIndexForExternalDrop(
                over.id.toString(),
                over.data?.current,
                optimisticSlots,
                rowN,
              );
              if (resolved && resolved.dateStr === overDateStr) {
                setCalendarInsertHighlight({ dateStr: resolved.dateStr, insertIndex: resolved.insertIndex });
              } else {
                setCalendarInsertHighlight(null);
              }
            }
          } else {
            const rowN = getCalendarRowIdsForDateStr(overDateStr).length;
            const resolved = resolveInsertIndexForExternalDrop(
              over.id.toString(),
              over.data?.current,
              optimisticSlots,
              rowN,
            );
            if (resolved && resolved.dateStr === overDateStr) {
              setCalendarInsertHighlight({ dateStr: resolved.dateStr, insertIndex: resolved.insertIndex });
            } else {
              setCalendarInsertHighlight(null);
            }
          }
          setDragOverDateCell(overDateStr);
          const contentW = measureCalendarDayContentWidthByDateStr(overDateStr);
          if (contentW != null) setSceneDragOverlayWidth(contentW);
        }
      }
      
      setInvalidDropTargets(newInvalidTargets);
    } else if (dragData && dragData.type === 'wholeDay') {
      // 整日拖拽：源日期不加入 invalidDropTargets，避免来源格出现「禁止拖入」遮罩；松手同日落点仍由 dragEnd 忽略
      const sourceDateStr = dragData.sourceDateStr;
      if (!over) {
        setDragOverDateCell(null);
      } else {
        const overDateStr = extractDateFromOverIdFn(over.id.toString(), over.data?.current as any);
        if (!overDateStr) {
          setDragOverDateCell(null);
        } else if (overDateStr === sourceDateStr) {
          // 来源日：不显示 drag-over / 禁止遮罩，仅作「从此日拖出」
          setDragOverDateCell(null);
        } else {
          setDragOverDateCell(overDateStr);
        }
      }
      setCalendarInsertHighlight(null);
      if (sourceDateStr) {
        setDisabledDateCells(new Set([sourceDateStr]));
      }
      setInvalidDropTargets(new Set());
    } else if (dragData && (dragData.type === 'clipboard' || dragData.type === 'clipboardGroup')) {
      if (!over) {
        setDragOverDateCell(null);
      } else {
        const overDateStr = extractDateFromOverIdFn(over.id.toString(), over.data?.current as any);
        setDragOverDateCell(overDateStr ?? null);
      }
      setCalendarInsertHighlight(null);
      setDisabledDateCells(new Set());
      setInvalidDropTargets(new Set());
    } else {
      setDragOverDateCell(null);
      setCalendarInsertHighlight(null);
      setDisabledDateCells(new Set());
      setInvalidDropTargets(new Set());
    }
  }, [
    extractDateFromOverIdFn,
    checkSlotDuplicateFn,
    getCalendarRowIdsForDateStr,
    optimisticSlots,
    measureCalendarDayContentWidthByDateStr,
    setDisabledDateCells,
    setDragOverDateCell,
    setInvalidDropTargets,
    setCalendarInsertHighlight,
    setSceneDragOverlayWidth,
  ]);

  // 不再需要 checkAndUpdateStartDate，统一在 ScheduleDetailsContent 中通过 useEffect 监听 slots 变化自动更新

  const handleCrossDateDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    const dragData = active.data.current;
    const slotBundleIdsSnapshot: string[] | null =
      dragData?.type === 'slot'
        ? slotDragBundleIdsRef.current
          ? [...slotDragBundleIdsRef.current]
          : [String(active.id)]
        : null;

    const writeMoveToCache = (nextSlots: SlotInfo[], nextCalDates: CalDateInfo[]) => {
      if (!scheduleID) return;
      const slotCache = getSlotCache(scheduleID);
      const calDateCache = getCalDateCache(scheduleID);
      if (!slotCache && !calDateCache) return;
      const committed = commitCrossDateMoveCache({
        nextSlots,
        baselineSlotIDs: new Set(optimisticSlots.map((slot) => slot.slotID)),
        cacheSlots: slotCache?.slots,
        nextCalDates,
        baselineDateStrs: new Set(
          optimisticCalDates
            .map((pd) => pd.calDateDt?.split('T')[0])
            .filter((dateStr): dateStr is string => !!dateStr),
        ),
        cacheCalDates: calDateCache?.calDates,
      });
      if (slotCache) setSlotCache(scheduleID, { slots: committed.slots });
      if (calDateCache) setCalDateCache(scheduleID, { calDates: committed.calDates });
    };

    const refreshConflictCalDates = async (error: unknown) => {
      if (!scheduleID || (error as { response?: { status?: number } })?.response?.status !== 409) return;
      try {
        const response = await getCalDatesByScheduleId(scheduleID);
        setCalDateCache(scheduleID, { calDates: response.data });
      } catch (refreshError) {
        console.error('刷新排期版本失败:', refreshError);
      }
    };

    try {
    // 重置隐藏标志（如果之前设置了）
    setShouldHideDragOverlay(false);
    
    // 重置其他状态（但保留拖拽预览状态，等成功后再清除）
    setDragOverDateCell(null);
    setCalendarInsertHighlight(null);
    setSourceDateCell(null);
    setActiveSortingId(null);
    setActiveSortingType(null);
    setActiveSortingDate(null);
    setActiveSortingDragLevel(null);
    setDisabledDateCells(new Set());
    setInvalidDropTargets(new Set());
    setWholeDayOverlayWidth(null);
    setSceneDragOverlayWidth(null);
    
    if (!over || !scheduleID || !dragData) {
      // 如果没有有效目标，清除拖拽预览状态
      setActiveDragId(null);
      setActiveDragType(null);
      setActiveDragData(null);
      return;
    }
    
    const overId = over.id.toString();
    const clipboardDroppableId = getClipboardDroppableId(scheduleID!);

    // 1. 从日历拖拽到剪贴板：添加缓存并删除原数据
    if (overId === clipboardDroppableId && (dragData.type === 'slot' || dragData.type === 'wholeDay')) {
      const sourceDateStr = dragData.sourceDateStr;
      let slotsToClip: SlotInfo[];
      let sourceCalDateSlotsSort: string[] | undefined;

      if (dragData.type === 'wholeDay') {
        slotsToClip = (dragData.slots as SlotInfo[]) ?? [];
        sourceCalDateSlotsSort = dragData.calDateSlotsSort;
      } else {
        const ids =
          slotBundleIdsSnapshot?.length && slotBundleIdsSnapshot.length > 0
            ? slotBundleIdsSnapshot
            : dragData.slot
              ? [dragData.slot.slotID]
              : [];
        slotsToClip = ids
          .map((tid) => optimisticSlots.find((t) => t.slotID === tid))
          .filter((t): t is SlotInfo => t != null);
        const sourceCalDateForSort = getDateCalDate(sourceDateStr || '');
        sourceCalDateSlotsSort = sourceCalDateForSort?.calDateSlotsSort;
      }

      if (slotsToClip.length > 0) {
        const sourceCalDate = sourceDateStr ? getDateCalDate(sourceDateStr) : undefined;
        const items = slotsToClipboardItems(
          slotsToClip,
          sourceCalDateSlotsSort,
          getSceneInfo
        );
        clipboardAddGroup({
          sourceDateStr: sourceDateStr ?? '',
          items,
          sourceCalDateShift: sourceCalDate?.calDateShift,
        });

        const idsToDelete = slotsToClip.map((t) => t.slotID);
        setShouldHideDragOverlay(true);
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);

        setOptimisticSlots((prev) =>
          prev.filter((t) => !idsToDelete.includes(t.slotID))
        );
        const idSet = new Set(idsToDelete);
        const newSourceFlat = normalizeToSortedIds(sourceCalDate?.calDateSlotsSort).filter((id) => !idSet.has(id));

        setOptimisticCalDates((prev) =>
          prev
            .map((pd) => {
              if (!pd.calDateDt || pd.calDateDt.split('T')[0] !== sourceDateStr) return pd;
              if (newSourceFlat.length === 0) return null;
              return { ...pd, calDateSlotsSort: newSourceFlat };
            })
            .filter((pd): pd is CalDateInfo => pd != null)
        );

        try {
          for (const id of idsToDelete) {
            await deleteSlotData(scheduleID!, id);
          }
          if (sourceCalDate?.calDateID && !sourceCalDate.calDateID.startsWith('temp-')) {
            if (newSourceFlat.length === 0) {
              await deleteCalDateData(scheduleID!, sourceCalDate.calDateID);
            } else {
              await upsertCalDateData(scheduleID!, {
                calDateDt: sourceDateStr,
                calDateBelongScheduleID: scheduleID,
                calDateSlotsSort: newSourceFlat,
              });
            }
          }
          refreshScheduleCalendarData();
        } catch (e) {
          console.error('剪贴板添加失败:', e);
          refreshScheduleCalendarData();
        }
      }
      return;
    }

    // 2. 从剪贴板整组拖拽到日历
    if (dragData.type === 'clipboardGroup' && Array.isArray(dragData.clipboardGroupItems)) {
      const targetDateStr = extractDateFromOverIdFn(overId, over?.data?.current as any);
      if (!targetDateStr) {
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        return;
      }
      const validOver = isValidCalendarDropTarget(overId, optimisticSlots);
      if (!validOver) {
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        return;
      }

      const groupItems = dragData.clipboardGroupItems as ClipboardScheduleItem[];
      const groupIndex = dragData.groupIndex as number;

      setShouldHideDragOverlay(true);
      setActiveDragId(null);
      setActiveDragType(null);
      setActiveDragData(null);

      try {
        await restoreClipboardItemsToDate({
          scheduleID: scheduleID!,
          targetDateStr,
          items: groupItems,
          sourceCalDateShift: dragData.sourceCalDateShift as string | undefined,
          getDateCalDate,
          createSlotData,
          updateCalDateSlotsSort,
        });
        clipboardRemoveGroupAt(groupIndex);
        refreshScheduleCalendarData();
        setCurrentDate(parseDate(targetDateStr));
        setServerSyncedField('userLastUsedCalendarDate', `${scheduleID};${targetDateStr}`);
      } catch (e) {
        console.error('从剪贴板恢复失败:', e);
        refreshScheduleCalendarData();
      }
      return;
    }

    // 3. 从剪贴板单条（组内卡片）拖到日历
    if (dragData.type === 'clipboard' && dragData.clipboardItem) {
      const targetDateStr = extractDateFromOverIdFn(overId, over?.data?.current as any);
      if (!targetDateStr) {
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        return;
      }
      const validOver = isValidCalendarDropTarget(overId, optimisticSlots);
      if (!validOver) {
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        return;
      }

      const item = dragData.clipboardItem as ClipboardScheduleItem;
      const groupIndex = dragData.groupIndex as number;
      const itemIndex = dragData.itemIndex as number;

      setShouldHideDragOverlay(true);
      setActiveDragId(null);
      setActiveDragType(null);
      setActiveDragData(null);

      try {
        await restoreClipboardItemsToDate({
          scheduleID: scheduleID!,
          targetDateStr,
          items: [item],
          sourceCalDateShift: dragData.sourceCalDateShift as string | undefined,
          getDateCalDate,
          createSlotData,
          updateCalDateSlotsSort,
        });
        const slotIndex = dragData.slotIndex;
        if (typeof slotIndex === 'number') {
          clipboardRemoveGroupItemSlotAt(groupIndex, itemIndex, slotIndex);
        } else {
          clipboardRemoveGroupItemAt(groupIndex, itemIndex);
        }
        refreshScheduleCalendarData();
        setCurrentDate(parseDate(targetDateStr));
        setServerSyncedField('userLastUsedCalendarDate', `${scheduleID};${targetDateStr}`);
      } catch (e) {
        console.error('从剪贴板恢复失败:', e);
        refreshScheduleCalendarData();
      }
      return;
    }

    // 处理场次拖拽（创建 slot）
    if (dragData.type === 'scene') {
      const targetDateStr = extractDateFromOverIdFn(overId, over?.data?.current as any);
      if (!targetDateStr) {
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        return;
      }
      // 落点与跨日拖拽一致：calendar-day-、segment/note 前缀、slotID 均可，extractDateFromOverIdFn 已提取日期

      const { scene, scheduleID: sceneScheduleID } = dragData;
      if (!scene || !sceneScheduleID) {
        console.warn('场次拖拽数据不完整:', { scene, sceneScheduleID });
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        return;
      }
      
      // 检查是否重复
      const isDuplicate = checkSlotDuplicateFn(scene.sceneID, targetDateStr, undefined, sceneScheduleID);
      if (isDuplicate) {
        console.warn('该场次在此日期已存在，无法创建');
        // 重复时清除拖拽预览状态，让 DragOverlay 播放回到原位置的动画
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        return;
      }
      
      // 立即同步清除拖拽预览状态，让预览卡片立即消失（在异步操作之前）
      // 先设置隐藏标志，然后立即清除状态，确保 DragOverlay 立即消失
      setShouldHideDragOverlay(true);
      setActiveDragId(null);
      setActiveDragType(null);
      setActiveDragData(null);
      
      try {
        // 创建 slot（包含完整的场次信息，与点击添加按钮的逻辑保持一致）
        // 使用统一的更新 hook，自动更新缓存和通知订阅者
        const newSlotID = await createSlotData(scheduleID!, {
          slotType: SlotType.SHOOT,
          slotName: scene.scenePlotSummary,
          slotSceneCode: scene.sceneCode,
          slotBelongSceneID: scene.sceneID,
          slotBelongScheduleID: scheduleID,
          slotBelongBreakdownID: scene.sceneBelongBreakdownID,
          slotLocation: scene.sceneLocation,
          slotStatus: SlotStatus.PREPARING,
          slotDate: targetDateStr,
          slotEndDay: targetDateStr,
        });
        
        if (newSlotID) {
          const rowOrderBefore = getCalendarRowIdsForDateStr(targetDateStr);
          const resolved = resolveInsertIndexForExternalDrop(
            overId,
            over?.data?.current,
            optimisticSlots,
            rowOrderBefore.length,
          );
          const insertIdx = resolved?.insertIndex ?? rowOrderBefore.length;
          const newFlat = [...rowOrderBefore];
          newFlat.splice(insertIdx, 0, newSlotID);
          await updateCalDateSlotsSort(targetDateStr, undefined, undefined, newFlat);
        }
        
        // 刷新数据（缓存已自动更新，这里刷新是为了确保数据同步）
        refreshScheduleCalendarData();
        
        // 将目标日期设置为当前日期，更新用户行为记忆
        setCurrentDate(parseDate(targetDateStr));
        setServerSyncedField('userLastUsedCalendarDate', `${scheduleID};${targetDateStr}`);
        
        // 自动检查并更新开机日期（如果新创建的 slot 日期早于当前开机日期）
        // createSlotData 已更新缓存，optimisticSlots 会通过订阅自动更新
        // 不再需要手动调用 checkAndUpdateStartDate，ScheduleDetailsContent 会通过 useEffect 监听 slots 变化自动更新
      } catch (error) {
        console.error('创建 slot 失败:', error);
        refreshScheduleCalendarData();
      }
      return;
    }
    
    // 处理整日拖拽
    if (dragData.type === 'wholeDay') {
      const targetDateStr = extractDateFromOverIdFn(overId, over?.data?.current as any);
      const sourceDateStr = dragData.sourceDateStr;
      const slotsToMove = dragData.slots as SlotInfo[] | undefined;
      const sourceCalDateSlotsSort = dragData.calDateSlotsSort;

      if (!targetDateStr || !sourceDateStr || !slotsToMove?.length) {
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        return;
      }
      if (targetDateStr === sourceDateStr) {
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        return;
      }
      const validOver =
        overId.startsWith('calendar-day-') ||
        overId.includes('__note-') ||
        overId.startsWith('note-') ||
        !!optimisticSlots.find((t) => t.slotID === overId);
      if (!validOver) {
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        return;
      }

      setShouldHideDragOverlay(true);
      setActiveDragId(null);
      setActiveDragType(null);
      setActiveDragData(null);

      const slotMap = new Map(optimisticSlots.map((t) => [t.slotID, t]));
      const targetCalDate = getDateCalDate(targetDateStr);
      const sourceCalDate = getDateCalDate(sourceDateStr);
      const mergedTargetCalDateShift = mergeCalDateShift(
        targetCalDate?.calDateShift,
        sourceCalDate?.calDateShift
      );
      const moveAllIds = new Set(slotsToMove.map((t) => t.slotID));
      const sourceOrder = normalizeToSortedIds(sourceCalDateSlotsSort).filter((id) => moveAllIds.has(id));
      const inSort = new Set(sourceOrder);
      const extras = slotsToMove.map((t) => t.slotID).filter((id) => !inSort.has(id));
      const orderedIds = [...sourceOrder, ...extras];

      const targetFlatBase = normalizeToSortedIds(targetCalDate?.calDateSlotsSort);
      const targetSceneIds = new Set(
        targetFlatBase
          .map((id) => slotMap.get(id)?.slotBelongSceneID)
          .filter((x): x is string => !!x),
      );
      const idsToMove: string[] = [];
      const idsToDelete: string[] = [];
      for (const id of orderedIds) {
        const t = slotMap.get(id);
        const isSolt = t && (t.slotType === SlotType.SHOOT || !t.slotType);
        if (isSolt && t.slotBelongSceneID && targetSceneIds.has(t.slotBelongSceneID)) {
          idsToDelete.push(id);
        } else {
          idsToMove.push(id);
          if (t?.slotBelongSceneID) targetSceneIds.add(t.slotBelongSceneID);
        }
      }
      const effMove = new Set([...idsToMove, ...idsToDelete]);
      const newTargetFlat = [...targetFlatBase.filter((id) => !effMove.has(id)), ...idsToMove];
      const nextSlots = buildMovedSlotList(optimisticSlots, idsToMove, idsToDelete, targetDateStr);
      const nextCalDates = buildMovedCalDateList(optimisticCalDates, {
        scheduleID: scheduleID!,
        sourceDateStr,
        targetDateStr,
        newSourceFlat: [],
        newTargetFlat,
        removeSourceDate: true,
        targetShift: mergedTargetCalDateShift,
      });
      setOptimisticSlots(nextSlots);
      setOptimisticCalDates(nextCalDates);

      try {
        const response = await moveSlots({
          scheduleID: scheduleID!,
          sourceDate: sourceDateStr,
          targetDate: targetDateStr,
          updates: idsToMove.map((slotID) => ({ slotID })),
          moveSlotIDs: idsToMove,
          deleteSlotIDs: idsToDelete,
          sourceSort: [],
          targetSort: newTargetFlat,
          targetShift: mergedTargetCalDateShift,
          expectedSourceUpdateTime: sourceCalDate?.calDateUpdateTime,
          expectedTargetUpdateTime: targetCalDate?.calDateUpdateTime,
        });
        writeMoveToCache(nextSlots, mergeServerCalDates(nextCalDates, response.data.calDates));
        refreshScheduleCalendarData();
        
        // 将目标日期设置为当前日期，更新用户行为记忆
        setCurrentDate(parseDate(targetDateStr));
        setServerSyncedField('userLastUsedCalendarDate', `${scheduleID};${targetDateStr}`);
      } catch (error) {
        console.error('整日拖拽失败:', error);
        await refreshConflictCalDates(error);
        refreshScheduleCalendarData();
      }
      return;
    }

    const sourceDateStr = dragData.sourceDateStr;
    
    if (!sourceDateStr) {
      // 清除拖拽预览状态
      setActiveDragId(null);
      setActiveDragType(null);
      setActiveDragData(null);
      return;
    }
    
    try {
      // 从 over.id 中提取日期字符串（支持日期单元格、地点组、slot）
      const targetDateStr = extractDateFromOverIdFn(overId, over?.data?.current as any);

      if (!targetDateStr) {
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        return;
      }

      if (targetDateStr === sourceDateStr && dragData.type === 'slot') {
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        await handleInDateSorting(
          active.id.toString(),
          overId,
          sourceDateStr,
          slotBundleIdsSnapshot,
        );
        return;
      }

      if (targetDateStr === sourceDateStr) {
        setActiveDragId(null);
        setActiveDragType(null);
        setActiveDragData(null);
        return;
      }

      if (dragData.type === 'slot') {
          setShouldHideDragOverlay(true);
          setActiveDragId(null);
          setActiveDragType(null);
          setActiveDragData(null);
          // 落点：日历格、插入缝/卡片半区（slot-insert__）、segment/note、slot 自身 id
          const validOver = isValidCalendarDropTarget(overId, optimisticSlots);
          if (!validOver) {
            setActiveDragId(null);
            setActiveDragType(null);
            setActiveDragData(null);
            return;
          }

          const moveIds =
            slotBundleIdsSnapshot?.length && slotBundleIdsSnapshot.length > 0
              ? slotBundleIdsSnapshot
              : dragData.slot
                ? [dragData.slot.slotID]
                : [];
          const slotsToMove: SlotInfo[] = moveIds
            .map((tid) => optimisticSlots.find((t) => t.slotID === tid))
            .filter((t): t is SlotInfo => t != null);
          if (slotsToMove.length === 0) {
            setActiveDragId(null);
            setActiveDragType(null);
            setActiveDragData(null);
            return;
          }

          const slotMap = new Map(optimisticSlots.map((t) => [t.slotID, t]));
          const sourceCalDate = getDateCalDate(sourceDateStr);
          const targetCalDate = getDateCalDate(targetDateStr);
          const moveSet = new Set(slotsToMove.map((t) => t.slotID));
          const sourceOrder = normalizeToSortedIds(sourceCalDate?.calDateSlotsSort).filter((id) => moveSet.has(id));
          const inSort = new Set(sourceOrder);
          const extras = slotsToMove.map((t) => t.slotID).filter((id) => !inSort.has(id));
          const orderedMove = [...sourceOrder, ...extras];

          const slotTypeItems = slotsToMove.filter(
            (t: SlotInfo) => t.slotType === SlotType.SHOOT || !t.slotType,
          );
          const isMultiSoltLocationBundle =
            slotBundleIdsSnapshot != null && slotBundleIdsSnapshot.length > 1;
          // 单卡：目标日已有同场次则整单取消（与悬停禁止一致）；多卡地点包：与整日拖拽相同，走下方 idsToDelete / idsToMove 拆分
          if (!isMultiSoltLocationBundle) {
            for (const slot of slotTypeItems) {
              if (!slot.slotBelongSceneID || !slot.slotBelongScheduleID) continue;
              if (
                checkSlotDuplicateFn(
                  slot.slotBelongSceneID,
                  targetDateStr,
                  slot.slotID,
                  slot.slotBelongScheduleID,
                )
              ) {
                console.warn('该场次在此日期已存在，无法移动');
                setActiveDragId(null);
                setActiveDragType(null);
                setActiveDragData(null);
                return;
              }
            }
          }

          const targetFlatBase = normalizeToSortedIds(targetCalDate?.calDateSlotsSort);
          const targetSceneIds = new Set(
            targetFlatBase
              .map((id) => slotMap.get(id)?.slotBelongSceneID)
              .filter((x): x is string => !!x),
          );
          const idsToMove: string[] = [];
          const idsToDelete: string[] = [];
          for (const id of orderedMove) {
            const t = slotMap.get(id);
            const isSolt = t && (t.slotType === SlotType.SHOOT || !t.slotType);
            if (isSolt && t.slotBelongSceneID && targetSceneIds.has(t.slotBelongSceneID)) {
              idsToDelete.push(id);
            } else {
              idsToMove.push(id);
              if (t?.slotBelongSceneID) targetSceneIds.add(t.slotBelongSceneID);
            }
          }
          const eff = new Set([...idsToMove, ...idsToDelete]);
          const rowOrderBefore = getCalendarRowIdsForDateStr(targetDateStr);
          const base = rowOrderBefore.filter((id) => !eff.has(id));
          const resolved = resolveInsertIndexForExternalDrop(
            overId,
            over?.data?.current,
            optimisticSlots,
            base.length,
          );
          const insertIdx = resolved?.insertIndex ?? base.length;
          const newTargetFlat = [...base.slice(0, insertIdx), ...idsToMove, ...base.slice(insertIdx)];
          const newSourceFlat = normalizeToSortedIds(sourceCalDate?.calDateSlotsSort).filter((id) => !eff.has(id));

          // 必须直接基于本次拖拽已经算出的两份新顺序确定地点头。若等待通用
          // normalizer 再读取 React refs，它可能仍看到 A2 属于源日的旧快照，
          // 从而漏掉目标日 A2=Y、B3=Y（拖回时则是 A2=N、B3=N）。
          const getSceneLocation = (sceneID: string | undefined) =>
            (getSceneInfo(sceneID)?.sceneLocation ?? '').trim();
          const visibilityPatchMap = new Map(
            [
              ...computeSlotLocationVisibilityPatchesForOrderedIds(
                newSourceFlat,
                optimisticSlots,
                getSceneLocation,
              ),
              ...computeSlotLocationVisibilityPatchesForOrderedIds(
                newTargetFlat,
                optimisticSlots,
                getSceneLocation,
              ),
            ].map((patch) => [patch.slotID, patch]),
          );

          const nextSlots = buildMovedSlotList(
            optimisticSlots,
            idsToMove,
            idsToDelete,
            targetDateStr,
            [...visibilityPatchMap.values()],
          );
          const nextCalDates = buildMovedCalDateList(optimisticCalDates, {
            scheduleID: scheduleID!,
            sourceDateStr,
            targetDateStr,
            newSourceFlat,
            newTargetFlat,
            removeSourceDate: false,
          });
          setOptimisticSlots(nextSlots);
          setOptimisticCalDates(nextCalDates);

          try {
            const slotUpdates = new Map<string, Partial<SlotInfo>>();
            for (const id of idsToMove) {
              slotUpdates.set(id, {
                slotDate: targetDateStr,
                slotEndDay: targetDateStr,
              });
            }
            for (const patch of visibilityPatchMap.values()) {
              slotUpdates.set(patch.slotID, {
                ...(slotUpdates.get(patch.slotID) ?? {}),
                slotLocationVisibility: patch.slotLocationVisibility,
                ...(patch.slotShift !== undefined ? { slotShift: patch.slotShift } : {}),
              });
            }
            const response = await moveSlots({
              scheduleID: scheduleID!,
              sourceDate: sourceDateStr,
              targetDate: targetDateStr,
              updates: [...slotUpdates].map(([slotID, updates]) => ({
                slotID,
                ...(updates.slotLocationVisibility === 'Y' || updates.slotLocationVisibility === 'N'
                  ? { slotLocationVisibility: updates.slotLocationVisibility }
                  : {}),
                ...(updates.slotShift !== undefined ? { slotShift: updates.slotShift } : {}),
              })),
              moveSlotIDs: idsToMove,
              deleteSlotIDs: idsToDelete,
              sourceSort: newSourceFlat,
              targetSort: newTargetFlat,
              expectedSourceUpdateTime: sourceCalDate?.calDateUpdateTime,
              expectedTargetUpdateTime: targetCalDate?.calDateUpdateTime,
            });
            writeMoveToCache(nextSlots, mergeServerCalDates(nextCalDates, response.data.calDates));
            refreshScheduleCalendarData();
            
            // 将目标日期设置为当前日期，更新用户行为记忆
            setCurrentDate(parseDate(targetDateStr));
            setServerSyncedField('userLastUsedCalendarDate', `${scheduleID};${targetDateStr}`);
          } catch (error) {
            console.error('处理拖拽失败:', error);
            await refreshConflictCalDates(error);
            refreshScheduleCalendarData();
          }
      }
    } catch (error) {
      console.error('处理拖拽失败:', error);
      // 出错时清除拖拽预览状态
      setActiveDragId(null);
      setActiveDragType(null);
      setActiveDragData(null);
    }
    } finally {
      slotDragBundleIdsRef.current = null;
      setActiveSlotDragBundleIds(null);
      setSlotOverlayWidth(null);
    }
  }, [
    scheduleID,
    extractDateFromOverIdFn,
    handleInDateSorting,
    checkSlotDuplicateFn,
    updateCalDateSlotsSort,
    refreshScheduleCalendarData,
    createSlotData,
    getDateCalDate,
    deleteSlotData,
    deleteCalDateData,
    upsertCalDateData,
    getSceneInfo,
    optimisticSlots,
    optimisticCalDates,
    getSlotCache,
    setSlotCache,
    getCalDateCache,
    setCalDateCache,
    getCalendarRowIdsForDateStr,
    setCurrentDate,
    setServerSyncedField,
    clipboardAddGroup,
    clipboardRemoveGroupAt,
    clipboardRemoveGroupItemAt,
    clipboardRemoveGroupItemSlotAt,
    setShouldHideDragOverlay,
    setDragOverDateCell,
    setCalendarInsertHighlight,
    setActiveDragId,
    setActiveDragType,
    setActiveDragData,
    setDisabledDateCells,
    setInvalidDropTargets,
    setActiveSortingId,
    setActiveSortingType,
    setActiveSortingDate,
    setActiveSortingDragLevel,
    setOptimisticCalDates,
    setOptimisticSlots,
    setSlotOverlayWidth,
    setSceneDragOverlayWidth,
    setSourceDateCell,
    setWholeDayOverlayWidth,
    setActiveSlotDragBundleIds,
  ]);
  return {
    onDragStart: handleCrossDateDragStart,
    onDragOver: handleCrossDateDragOver,
    onDragEnd: handleCrossDateDragEnd,
  };
}
